import { Box } from '@mui/material';
import { useAppDispatch } from '../../store/hooks';
import { createNotification } from '../../store/notifications/notification-slice';
import { useEffect, useState } from 'react';
import { DocumentService } from '../../Service/DocumentService';
import { Loading } from '../../components/Loading';
import StrategyDocuments from '../../components/StrategyDocuments/StrategyDocuments';
import { useOutletContext, useParams } from 'react-router-dom';
import { Steps } from '../../components/Steps';

const AdminDocuments = () => {
  const { userId } = useParams();
  const [clientDetails] = useOutletContext<any>();
  const [strategies, setStrategies] = useState<any[]>([]);
  const [additionalDocuments, setAdditionalDocuments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const appDispatch = useAppDispatch();

  useEffect(() => {
    getStrategies();
  }, []);

  const getStrategies = async () => {
    setIsLoading(true);
    try {
      const response = await Promise.all([
        DocumentService.getUserDocumentsAdmin(userId!),
        DocumentService.getAdditionalDocumentsAdmin(userId!),
      ]);

      const [{ data: allStrategies }, { data: allAdditionalDocuments }] =
        response;

      setStrategies(allStrategies);
      setAdditionalDocuments(allAdditionalDocuments);
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadStrategyRequiredDocument = async (
    strategyId: string,
    name: string
  ) => {
    try {
      const { data } = await DocumentService.uploadRequiredDocument(
        name,
        strategyId,
        userId!
      );

      const newStrategies = structuredClone(strategies);
      const strategy = newStrategies.find((str: any) => str.id === strategyId);
      strategy.requiredDocuments.push(data);
      setStrategies(newStrategies);

      appDispatch(
        createNotification({
          type: 'success',
          message: `Required document '${data.name}' has been added!`,
        })
      );
    } catch {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
    }
  };

  const uploadOtherRequiredDocument = async (name: string) => {
    try {
      const { data } = await DocumentService.uploadRequiredDocument(
        name,
        null,
        userId!
      );

      const newAdditionalDocuments = [...additionalDocuments];
      newAdditionalDocuments.push(data);
      setAdditionalDocuments(newAdditionalDocuments);

      appDispatch(
        createNotification({
          type: 'success',
          message: `Required document ${data.name} has been added!`,
        })
      );
    } catch {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
    }
  };

  const handleRequiredDocumentDelete = async (
    strategyId: string,
    reqDocId: string
  ) => {
    try {
      await DocumentService.deleteRequiredDocument(reqDocId);

      const newStrategies = structuredClone(strategies);
      const strategy = newStrategies.find((str: any) => str.id === strategyId);
      strategy.requiredDocuments = strategy.requiredDocuments.filter(
        (reqDoc: any) => reqDoc.id !== reqDocId
      );
      setStrategies(newStrategies);
      appDispatch(
        createNotification({
          type: 'success',
          message: 'Required document has been removed!',
        })
      );
    } catch {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
    }
  };

  const handleOtherRequiredDocumentDelete = async (reqDocId: string) => {
    try {
      await DocumentService.deleteRequiredDocument(reqDocId);

      const newAdditionalDocuments = additionalDocuments.filter(
        (reqDoc: any) => reqDoc.id !== reqDocId
      );
      setAdditionalDocuments(newAdditionalDocuments);

      appDispatch(
        createNotification({
          type: 'success',
          message: 'Required document has been removed!',
        })
      );
    } catch {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '36px',
        padding: '32px 36px',
      }}
    >
      <Steps
        items={[
          `${clientDetails?.name} ${clientDetails?.lastName}`,
          'Documents',
        ]}
      />
      <Loading isLoading={isLoading}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          {strategies.map(strategy => {
            return (
              <StrategyDocuments
                key={strategy.id}
                requiredDocuments={strategy.requiredDocuments}
                title={strategy.name}
                description={`Required documents for the ${strategy.name} strategy`}
                handleRequiredDocumentUpload={name =>
                  uploadStrategyRequiredDocument(strategy.id, name)
                }
                handleRequiredDocumentDelete={reqDocId =>
                  handleRequiredDocumentDelete(strategy.id, reqDocId)
                }
              />
            );
          })}
          <StrategyDocuments
            requiredDocuments={additionalDocuments}
            title='Additional Documents'
            description='All documents that don’t belong to a specific strategy'
            handleRequiredDocumentUpload={uploadOtherRequiredDocument}
            handleRequiredDocumentDelete={handleOtherRequiredDocumentDelete}
          />
        </Box>
      </Loading>
    </Box>
  );
};

export default AdminDocuments;
